import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@src/components/Card';
import apis from '@src/apis';
import moment from 'moment';
import 'moment/locale/vi';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import DeviceSVG from '@src/assets/icons/devices.svg';
import QuestionMark from '@src/assets/icons/QuestionMark.svg';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';

import TabletAndroidRoundedIcon from '@mui/icons-material/TabletAndroidRounded';
import NotificationDialog from '@src/components/NewNotificationDialog';
import Logout from '@src/assets/icons/logout.svg';
import { NOTIFICATION_URL } from '@src/configs';
import actions from '@src/redux/actions';
import OTPConfirm from '@src/components/OTPConfirm';
import { sortBy } from 'lodash';
import { DEVICE_STATUS } from '@src/constants/device';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  StyledManagerDevice,
  StyledOSName,
  StyledWapper,
  StyledActionWarning,
} from './index.styled';

const NewManageDevice = ({
  dialogDevice,
  isPopup,
  isExceedDevices,
  checkUserDevice,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const userInfo = useSelector((state) => state.auth.user || {});
  const { fingerprint, status = '' } = useSelector(
    (state) => state.deviceV2 || {},
  );
  const [devices, setDevices] = useState([]);
  const [totalFetchDevices, setTotalFetchDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loginDevices, setLoginDevices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showConfirmLogoutDialog, setShowConfirmLogoutDialog] = useState(false);
  useState(false);
  const [showRequestLogutDevice, setShowRequestLogutDevice] = useState(false);

  const responseDevice = useSelector((state) => state.device);
  const dispatch = useDispatch();

  const ws = useRef(null);

  const fetchDevices = async () => {
    setLoading(true);
    const data = await apis.devices.getDevices({
      query: {
        'status[$in]': [DEVICE_STATUS.ONLINE, DEVICE_STATUS.RESTRICTED],
      },
    });
    if (data?.status) {
      // List all devices that are online or restricted with the current device
      const listDevice = data?.result?.devices?.filter(
        (device) =>
          device?.status === DEVICE_STATUS.ONLINE ||
          device?.fingerprint === fingerprint,
      );

      setDevices(listDevice);
      setTotalFetchDevices([...new Set([...devices, ...listDevice])]);
      setLoginDevices(listDevice.length);
    }
    setLoading(false);
  };

  const { getFeatureValue } = useFeatureFlags();
  const useUsageOptions = getFeatureValue(
    FEATURE_KEYS.USE_USAGE_OPTIONS_IN_PACKAGE,
    { userId: userInfo.id, packageCode: userInfo.packageCode },
  );

  const maxDevices = useUsageOptions
    ? userInfo?.studioUsageOptions?.device
    : userInfo?.maxDevices;

  const logoutDeviceWithOTP = async (otp) => {
    const result = await apis.devices.logoutWithOTP(otp);
    setShowRequestLogutDevice(!result);
    if (result) {
      await fetchDevices();
      if (isPopup) {
        checkUserDevice();
      }
    }
    return result;
  };

  const callApiLogoutDevices = async (logoutDevice) => {
    const deviceId = logoutDevice?.id;
    setShowRequestLogutDevice(true);
    await apis.devices.sendEmailConfirmLogoutDevices(userInfo.email, [
      deviceId,
    ]);
  };

  const renderLastOnlineAt = (timeLastOnline, locale = 'vn') => {
    if (!timeLastOnline) return '';
    moment.locale(locale);

    const now = moment();
    const timeDiffInHours = now.diff(timeLastOnline, 'hours', true);

    if (timeDiffInHours > 24) {
      return moment(timeLastOnline).format('DD/MM/YYYY');
    }

    return moment(timeLastOnline).fromNow(); // It will automatically adapt to the locale
  };

  const renderDeviceInfo = (device, isCurrentDevice = false) => {
    const { city, regionName, region, country, countryName } = device || {};
    const deviceCity = city || regionName || region || '';
    const deviceCountry = countryName || country || '';

    return (
      <div className="info-device-wrapper">
        <div className="os-info">
          <span className="os-name">{`${device?.os?.name}${
            device?.client?.name ? ` - ${device?.client?.name}` : ''
          }`}</span>
        </div>
        <div className="des-device">
          <div className="des-device-detail">
            <span>
              {deviceCity}
              {deviceCity && deviceCountry && ', '}
              {deviceCountry}
            </span>
            <div className="icon-rounded">•</div>
            <span>{device?.ip}</span>
          </div>
          {!isPopup && !isCurrentDevice && (
            <div className="icon-rounded responsive">•</div>
          )}
          {!isCurrentDevice && (
            <span>
              {t('lastOnlineAtV2')}{' '}
              {renderLastOnlineAt(
                device?.lastOnlineAt || device?.createdAt,
                language,
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const getCurrentDevice = (device) => {
    const DEVICE = {
      DESKTOP: 'desktop',
      SMARTPHONE: 'smartphone',
      TABLET: 'tablet',
      TELEVISION: 'television',
      APP: 'app',
    };

    const ICONS_DEVICE = {
      [DEVICE.DESKTOP]: <DesktopWindowsRoundedIcon />,
      [DEVICE.SMARTPHONE]: <PhoneAndroidRoundedIcon />,
      [DEVICE.TABLET]: <TabletAndroidRoundedIcon />,
      [DEVICE.TELEVISION]: <TvRoundedIcon />,
      [DEVICE.APP]: <img src={QuestionMark} alt="QuestionMark" />,
    };

    return ICONS_DEVICE[device] || ICONS_DEVICE[DEVICE.APP];
  };

  const handleShowConfirmLogoutDialog = (deviceInfo) => {
    setSelectedDevice(deviceInfo);
    setShowConfirmLogoutDialog(true);
  };

  const handleCloseConfirmLogoutDialog = () => {
    setSelectedDevice(null);
    setShowConfirmLogoutDialog(false);
  };

  const handleConfirmLogoutDevices = async () => {
    setShowConfirmLogoutDialog(false);
    await callApiLogoutDevices(selectedDevice);
  };

  const renderConfirmLogoutDevicesAction = (handleClose, handleConfirm) => (
    <StyledActionWarning>
      <Button className="cancel-btn" variant="outlined" onClick={handleClose}>
        {t('cancel')}
      </Button>
      <Button className="agree-btn" variant="contained" onClick={handleConfirm}>
        {t('logout')}
      </Button>
    </StyledActionWarning>
  );

  const renderContentLogoutDevice = () => (
    <Typography>{t('logoutWarning')}</Typography>
  );

  useEffect(() => {
    if (fingerprint) fetchDevices();
  }, [fingerprint]);

  useEffect(() => {
    ws.current = new WebSocket(NOTIFICATION_URL);
  }, []);

  useEffect(() => {
    const newDevices = devices.map((device) => {
      if (
        responseDevice[device.fingerprint]?.status &&
        device.status !== responseDevice[device.fingerprint]?.status
      ) {
        return {
          ...device,
          status: responseDevice[device.fingerprint]?.status,
        };
      }
      return device;
    });

    const newTotalFetchDevices = totalFetchDevices.map((device) => {
      if (
        responseDevice[device.fingerprint]?.status &&
        device.status !== responseDevice[device.fingerprint]?.status
      ) {
        return {
          ...device,
          status: responseDevice[device.fingerprint]?.status,
        };
      }
      return device;
    });

    setDevices(newDevices);
    setTotalFetchDevices(newTotalFetchDevices);
    dispatch(actions.device.removeDevice(Object.keys(responseDevice)));
  }, [responseDevice]);

  return (
    <>
      <StyledWapper>
        <Card className="device-list">
          <StyledManagerDevice
            isPopup={isPopup}
            isExceedDevices={
              isExceedDevices || status === DEVICE_STATUS.RESTRICTED
            }
            dialogDevice={dialogDevice}
          >
            <div className="manager-device-header">
              {!dialogDevice ? (
                <div className="title">{t('manageDevice')}</div>
              ) : (
                <div className="noti-error">
                  <ErrorRoundedIcon className="info-error-icon" />
                  {t('signOutOfADeviceNoti')}
                </div>
              )}
              <div className="info-device">
                <img className="device-svg" src={DeviceSVG} alt="device" />
                <div className="title-device">{t('mydevice')}:</div>
                <div className="total-device">
                  {loginDevices?.toLocaleString(language)}/
                  {maxDevices?.toLocaleString(language)} {t('device')}
                </div>
              </div>
            </div>
            {loading ? (
              <CircularProgress />
            ) : (
              <div className="table-device">
                {sortBy(
                  [...devices],
                  (item) => fingerprint !== item?.fingerprint,
                )?.map((device) => (
                  <div className="item-device-info">
                    <div className="icon-device">
                      {getCurrentDevice(device?.device?.type)}
                    </div>
                    {/* <div className="item-device"> */}
                    <div className="item-name-device">
                      {renderDeviceInfo(
                        device,
                        device?.fingerprint === fingerprint,
                      )}
                    </div>
                    {device?.fingerprint === fingerprint && (
                      <StyledOSName>
                        <div className="blue-dot" />
                        <div>{t('thisDevice')}</div>
                      </StyledOSName>
                    )}
                    {fingerprint !== device?.fingerprint && (
                      <Tooltip
                        arrow
                        title={
                          <span className="tooltip-text">{t('logout')}</span>
                        }
                        placement="left"
                      >
                        <div
                          tabIndex="0"
                          onClick={() => handleShowConfirmLogoutDialog(device)}
                          role="button"
                          className="btn-logout"
                        >
                          <img src={Logout} alt="logout" />
                        </div>
                      </Tooltip>
                    )}
                    {/* </div> */}
                  </div>
                ))}
              </div>
            )}
          </StyledManagerDevice>
        </Card>
      </StyledWapper>

      <NotificationDialog
        name="ssml"
        dialogDevice
        title={t('confirmLogoutDevicesTitle')}
        variant="warning"
        open={showConfirmLogoutDialog}
        onClose={handleCloseConfirmLogoutDialog}
        actionComponent={renderConfirmLogoutDevicesAction(
          handleCloseConfirmLogoutDialog,
          handleConfirmLogoutDevices,
        )}
        contentComponent={renderContentLogoutDevice()}
      />

      {showRequestLogutDevice && (
        <OTPConfirm
          open={showRequestLogutDevice}
          onClose={() => setShowRequestLogutDevice(false)}
          onSubmit={logoutDeviceWithOTP}
          destination={userInfo?.email}
          onResendOTP={handleConfirmLogoutDevices}
        />
      )}
    </>
  );
};

export default NewManageDevice;
